import React, { createContext, useContext, useEffect, useReducer, ReactNode } from 'react';
import { AuthState, AuthContextType, LoginCredentials, RegisterData, User } from '../types/auth';
import { Order, CreateOrderData } from '../types/order';
import { apiRequest } from '../lib/api';
import toast from 'react-hot-toast';
import { AUTH_ENABLED, ORDERS_ENABLED } from '../config/features';

type AuthAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_USER'; payload: User | null }
  | { type: 'SET_INITIALIZED'; payload: boolean };

const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_USER':
      return { ...state, user: action.payload };
    case 'SET_INITIALIZED':
      return { ...state, initialized: action.payload };
    default:
      return state;
  }
};

const initialState: AuthState = {
  user: null,
  loading: false,
  initialized: false,
};

interface AuthResponse {
  user: User;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  useEffect(() => {
    const initializeAuth = async () => {
      if (!AUTH_ENABLED) {
        dispatch({ type: 'SET_USER', payload: null });
        dispatch({ type: 'SET_LOADING', payload: false });
        dispatch({ type: 'SET_INITIALIZED', payload: true });
        return;
      }

      try {
        const data = await apiRequest<AuthResponse | { user: null }>('/auth/me');
        dispatch({ type: 'SET_USER', payload: data.user });
      } catch (error) {
        dispatch({ type: 'SET_USER', payload: null });
      } finally {
        dispatch({ type: 'SET_LOADING', payload: false });
        dispatch({ type: 'SET_INITIALIZED', payload: true });
      }
    };

    initializeAuth();
  }, []);

  const signIn = async (credentials: LoginCredentials) => {
    if (!AUTH_ENABLED) {
      toast.error('Личный кабинет временно недоступен');
      return;
    }

    dispatch({ type: 'SET_LOADING', payload: true });

    try {
      const data = await apiRequest<AuthResponse>('/auth/login', {
        method: 'POST',
        body: JSON.stringify(credentials),
      });

      dispatch({ type: 'SET_USER', payload: data.user });
      toast.success('Добро пожаловать!');
    } catch (error: any) {
      toast.error(error.message || 'Ошибка входа');
      throw error;
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const signUp = async (data: RegisterData) => {
    if (!AUTH_ENABLED) {
      toast.error('Регистрация временно недоступна');
      return;
    }

    dispatch({ type: 'SET_LOADING', payload: true });

    try {
      const response = await apiRequest<AuthResponse>('/auth/register', {
        method: 'POST',
        body: JSON.stringify({
          email: data.email,
          password: data.password,
          full_name: data.full_name,
          personal_data_consent: data.personal_data_consent,
        }),
      });

      dispatch({ type: 'SET_USER', payload: response.user });
      toast.success('Регистрация успешна!');
    } catch (error: any) {
      toast.error(error.message || 'Ошибка регистрации');
      throw error;
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const signOut = async () => {
    if (!AUTH_ENABLED) {
      dispatch({ type: 'SET_USER', payload: null });
      return;
    }

    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      await apiRequest('/auth/logout', { method: 'POST' });
      dispatch({ type: 'SET_USER', payload: null });
      window.dispatchEvent(new CustomEvent('user-signed-out'));
      toast.success('Вы вышли из системы');
    } catch (error: any) {
      toast.error(error.message || 'Ошибка выхода');
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const signInWithOAuth = async () => {
    toast.error('Вход через Google временно недоступен');
  };

  const updateProfile = async (userData: Partial<User>) => {
    if (!AUTH_ENABLED) {
      toast.error('Личный кабинет временно недоступен');
      return;
    }

    try {
      if (!state.user) throw new Error('User not authenticated');

      dispatch({ type: 'SET_LOADING', payload: true });
      const data = await apiRequest<AuthResponse>('/auth/profile', {
        method: 'PATCH',
        body: JSON.stringify(userData),
      });

      dispatch({ type: 'SET_USER', payload: data.user });
      toast.success('Профиль обновлен');
    } catch (error: any) {
      toast.error(error.message || 'Ошибка обновления профиля');
      throw error;
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const changePassword = async (currentPassword: string, newPassword: string) => {
    if (!AUTH_ENABLED) {
      toast.error('Личный кабинет временно недоступен');
      return;
    }

    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      await apiRequest('/auth/password', {
        method: 'POST',
        body: JSON.stringify({ currentPassword, newPassword }),
      });
      toast.success('Пароль успешно изменен');
    } catch (error: any) {
      toast.error(error.message || 'Ошибка смены пароля');
      throw error;
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const createOrder = async (orderData: CreateOrderData): Promise<Order> => {
    if (!ORDERS_ENABLED) {
      throw new Error('Оформление заказов на сайте временно недоступно');
    }

    try {
      if (!state.user) throw new Error('User not authenticated');

      const data = await apiRequest<{ order: Order }>('/orders', {
        method: 'POST',
        body: JSON.stringify(orderData),
      });

      toast.success('Заказ успешно создан');
      return data.order;
    } catch (error: any) {
      toast.error(error.message || 'Ошибка создания заказа');
      throw error;
    }
  };

  const getUserOrders = async (): Promise<Order[]> => {
    if (!ORDERS_ENABLED) {
      return [];
    }

    try {
      if (!state.user) throw new Error('User not authenticated');
      const data = await apiRequest<{ orders: Order[] }>('/orders');
      return data.orders;
    } catch (error: any) {
      toast.error(error.message || 'Ошибка загрузки заказов');
      return [];
    }
  };

  const getOrderById = async (orderId: string): Promise<Order | null> => {
    if (!ORDERS_ENABLED) {
      return null;
    }

    try {
      if (!state.user) throw new Error('User not authenticated');
      const data = await apiRequest<{ order: Order }>(`/orders/${orderId}`);
      return data.order;
    } catch (error: any) {
      toast.error(error.message || 'Ошибка загрузки заказа');
      return null;
    }
  };

  const cancelOrder = async (orderId: string): Promise<void> => {
    if (!ORDERS_ENABLED) {
      toast.error('Оформление заказов на сайте временно недоступно');
      return;
    }

    try {
      if (!state.user) throw new Error('User not authenticated');
      await apiRequest(`/orders/${orderId}/cancel`, { method: 'PATCH' });
      toast.success('Заказ отменен');
    } catch (error: any) {
      toast.error(error.message || 'Ошибка отмены заказа');
      throw error;
    }
  };

  const value: AuthContextType = {
    user: state.user,
    loading: state.loading,
    initialized: state.initialized,
    signIn,
    signUp,
    signOut,
    signInWithOAuth,
    updateProfile,
    changePassword,
    createOrder,
    getUserOrders,
    getOrderById,
    cancelOrder,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
